import { brandColor } from '../../../_helpers/brand-color';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { SalesService } from '../../../_services/sales/sales.service';
import { SalesFilterService, SalesFilters } from '../sales-filter.service';
import ISale from '../../../_interfaces/ISale';

// PrimeNG
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { ChartModule } from 'primeng/chart';

interface SalesStats {
  totalSales: number;
  totalRevenue: number;
  averageTicket: number;
  salesByPaymentMethod: { [key: string]: { count: number; total: number } };
  salesByProduct: { name: string; count: number; total: number }[];
  dailySales: { date: string; count: number; total: number }[];
}

@Component({
  standalone: true,
  selector: 'app-sales-stats',
  templateUrl: './sales-stats.component.html',
  styleUrls: ['./sales-stats.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CardModule,
    SelectModule,
    SkeletonModule,
    ChartModule,
  ],
})
export class SalesStatsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  public loading: boolean = true;
  public stats: SalesStats | null = null;
  public sales: ISale[] = [];

  // Expose Object for template
  public objectKeys = Object.keys;

  // Chart data
  public revenueChartData: any;
  public paymentMethodChartData: any;
  public chartOptions: any;
  public pieChartOptions: any;

  private subscriptions: Subscription[] = [];

  constructor(
    public session: SessionService,
    private salesService: SalesService,
    private translate: TranslateService,
    private filterService: SalesFilterService,
  ) {
    super(session);
    this.initChartOptions();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadSales();

    // Subscribe to filter changes from parent
    this.subscriptions.push(
      this.filterService.filters$.subscribe((filters) => {
        this.applyFilters(filters);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initChartOptions(): void {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor =
      documentStyle.getPropertyValue('--p-text-color') || '#374151';
    const textColorSecondary =
      documentStyle.getPropertyValue('--p-text-muted-color') || '#6b7280';
    const surfaceBorder =
      documentStyle.getPropertyValue('--p-content-border-color') || '#e5e7eb';

    this.chartOptions = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
      },
    };

    this.pieChartOptions = {
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: textColor,
          },
        },
      },
    };
  }

  loadSales(): void {
    this.loading = true;

    // Fetch all sales and filter client-side
    this.salesService.findBySite(this.customer.id, this.site.id, {}).subscribe({
      next: (sales) => {
        this.sales = sales;
        this.applyFilters(this.filterService.currentFilters);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  applyFilters(filters: SalesFilters): void {
    let filtered = [...this.sales];

    // Client-side date filtering
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = moment(filters.dateRange[0]).startOf('day');
      const endDate = moment(filters.dateRange[1]).endOf('day');

      filtered = filtered.filter((sale) => {
        const saleDate = moment(sale.sales_date);
        return (
          saleDate.isSameOrAfter(startDate) && saleDate.isSameOrBefore(endDate)
        );
      });
    }

    // Text search
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      filtered = filtered.filter((sale) => {
        const patientName = sale.patient
          ? `${sale.patient.name} ${sale.patient.surname}`.toLowerCase()
          : '';
        const productName = sale.product?.name?.toLowerCase() || '';
        let treatmentName = '';
        if (sale.treatment) {
          if ((sale.treatment as any).treatment?.name) {
            treatmentName = (
              sale.treatment as any
            ).treatment.name.toLowerCase();
          } else if (sale.treatment.name) {
            treatmentName = sale.treatment.name.toLowerCase();
          }
        }
        return (
          patientName.includes(search) ||
          productName.includes(search) ||
          treatmentName.includes(search)
        );
      });
    }

    this.stats = this.calculateStats(filtered);
    this.updateCharts();
  }

  private calculateStats(sales: ISale[]): SalesStats {
    const totalSales = sales.length;
    const totalRevenue = sales.reduce((sum, sale) => sum + sale.price, 0);
    const averageTicket = totalSales > 0 ? totalRevenue / totalSales : 0;

    // Group by payment method
    const salesByPaymentMethod: {
      [key: string]: { count: number; total: number };
    } = {};
    sales.forEach((sale) => {
      const methodKey = sale.paymentType?.name || '';
      const method = this.translatePaymentMethod(methodKey);
      if (!salesByPaymentMethod[method]) {
        salesByPaymentMethod[method] = { count: 0, total: 0 };
      }
      salesByPaymentMethod[method].count++;
      salesByPaymentMethod[method].total += sale.price;
    });

    // Group by product/treatment
    const productMap: { [key: string]: { count: number; total: number } } = {};
    sales.forEach((sale) => {
      const name = sale.product?.name || sale.treatment?.name || 'Sin producto';
      if (!productMap[name]) {
        productMap[name] = { count: 0, total: 0 };
      }
      productMap[name].count++;
      productMap[name].total += sale.price;
    });

    const salesByProduct = Object.entries(productMap)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);

    // Group by date
    const dateMap: { [key: string]: { count: number; total: number } } = {};
    sales.forEach((sale) => {
      const date = moment(sale.sales_date).format('DD/MM');
      if (!dateMap[date]) {
        dateMap[date] = { count: 0, total: 0 };
      }
      dateMap[date].count++;
      dateMap[date].total += sale.price;
    });

    const dailySales = Object.entries(dateMap)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => {
        const dateA = moment(a.date, 'DD/MM');
        const dateB = moment(b.date, 'DD/MM');
        return dateA.diff(dateB);
      });

    return {
      totalSales,
      totalRevenue,
      averageTicket,
      salesByPaymentMethod,
      salesByProduct,
      dailySales,
    };
  }

  private updateCharts(): void {
    if (!this.stats) return;

    // Revenue chart
    this.revenueChartData = {
      labels: this.stats.dailySales.map((d) => d.date),
      datasets: [
        {
          label: 'Ingresos',
          data: this.stats.dailySales.map((d) => d.total),
          fill: true,
          backgroundColor: brandColor(500, 0.1),
          borderColor: brandColor(),
          tension: 0.4,
        },
      ],
    };

    // Payment method chart
    const methods = Object.keys(this.stats.salesByPaymentMethod);
    const methodTotals = methods.map(
      (m) => this.stats!.salesByPaymentMethod[m].total,
    );

    this.paymentMethodChartData = {
      labels: methods,
      datasets: [
        {
          data: methodTotals,
          backgroundColor: [
            brandColor(),
            '#10b981',
            '#f59e0b',
            '#ef4444',
            '#8b5cf6',
          ],
        },
      ],
    };
  }

  translatePaymentMethod(methodKey: string): string {
    if (!methodKey) {
      return this.translate.instant('COMMON.UNDEFINED') || 'Sin método';
    }

    const translationKey = `PAYMENT_TYPES.${methodKey.toUpperCase()}`;
    const translated = this.translate.instant(translationKey);

    // If translation exists and is different from key, use it
    return translated !== translationKey ? translated : methodKey;
  }

  formatCurrency(value: number): string {
    return value.toFixed(2) + ' €';
  }
}
